<template>
  <div class="sj-save-toggle-block">
    <sjwc-tooltip
      ref="tooltipRef"
      :content="tooltipContent"
      :persist-on-click="true"
    >
      <button 
        :aria-label="isContentSaved ? 'Saved' : 'Add to Saved List'"
        tabindex="0"
        data-jest="save-toggle-wrapper"
        class="sj-save-toggle-block__button"
        @click="toggleSaved"
        @keydown.space.prevent="toggleSaved"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
        @focus="handleFocus"
        @blur="handleBlur"
      >
        <sjwc-icon
          :name="iconName"
          aria-hidden="true"
          class="sj-save-toggle-block__icon fa-fw"
        />
      </button>
    </sjwc-tooltip>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, onUnmounted, Ref, ComputedRef } from 'vue';
import ApiService from '../../utils/services/api-service';

interface Props {
  publishedCourseId: string | null;
  publishedPathId: string | null;
  isContentSaved: boolean;
  saveContentTooltipTitle: string;
  saveContentTooltipMessage: string;
  removeSavedContentTooltipTitle: string;
  removeSavedContentTooltipMessage: string;
  removeSavedContentTooltipTitleSuccess: string;
  saveContentErrorMessage: string;
  removeSavedContentErrorMessage: string;
}

interface TooltipRef {
  showTooltip: () => void;
  hideTooltip: () => void;
}

interface ApiError {
  status?: number;
}

interface SaveParams {
  content_type: string;
  is_active: boolean;
  published_course_id?: string;
  published_path_id?: string;
}

const props = defineProps<Props>();

const isContentSaved: Ref<boolean> = ref(props.isContentSaved);
const loading: Ref<boolean> = ref(false);
const showRemovalMessage: Ref<boolean> = ref(false);
const showSavedMessage: Ref<boolean> = ref(false);
const tooltipRef: Ref<TooltipRef | null> = ref(null);
const isHovering: Ref<boolean> = ref(false);
const isFocused: Ref<boolean> = ref(false);
const messageSequenceTimeouts: Ref<number[]> = ref([]);

const iconName: ComputedRef<string> = computed(() => {
  if (loading.value) {
    return 'fa fa-spinner fa-spin';
  }

  return isContentSaved.value ? 'fa fa-bookmark' : 'fa fa-bookmark-o';
});

const tooltipContent: ComputedRef<string> = computed(() => {
  if (showRemovalMessage.value) {
    return `<strong>${props.removeSavedContentTooltipTitleSuccess}</strong><br>${props.removeSavedContentTooltipMessage}`;
  }

  if (isContentSaved.value || showSavedMessage.value) {
    return `<strong>${props.removeSavedContentTooltipTitle}</strong><br>${props.removeSavedContentTooltipMessage}`;
  }

  return `<strong>${props.saveContentTooltipTitle}</strong><br>${props.saveContentTooltipMessage}`;
});

const handleMouseEnter = () => {
  isHovering.value = true;

  // Cancel any running message sequences unless we're showing removal message
  if (!showRemovalMessage.value) {
    cancelMessageSequences();
  }
};

const handleMouseLeave = () => {
  isHovering.value = false;
  
  // Don't hide tooltip if we're showing removal message
  if (showRemovalMessage.value && tooltipRef.value) {
    // Keep tooltip visible during removal message
    return;
  }
};

const handleFocus = () => {
  isFocused.value = true;
};

const handleBlur = () => {
  isFocused.value = false;
};

const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => {
    const timeoutId = setTimeout(resolve, ms);
    messageSequenceTimeouts.value.push(timeoutId);
  });
};

const cancelMessageSequences = (): void => {
  // Clear all timeouts
  messageSequenceTimeouts.value.forEach(timeoutId => clearTimeout(timeoutId));
  messageSequenceTimeouts.value = [];

  // Reset message flags to show current state
  showRemovalMessage.value = false;
  showSavedMessage.value = false;
};

const showRemovalMessageSequence = async (): Promise<void> => {
  showRemovalMessage.value = true;

  await delay(100);

  if (tooltipRef.value) {
    tooltipRef.value.showTooltip();
  }

  await delay(2000);

  // Only hide if we're still showing the removal message
  if (tooltipRef.value && showRemovalMessage.value) {
    tooltipRef.value.hideTooltip();
  }

  await delay(300);

  showRemovalMessage.value = false;
  messageSequenceTimeouts.value = [];
};

const showSavedMessageSequence = async (): Promise<void> => {
  showSavedMessage.value = true;

  await delay(100);

  if (tooltipRef.value) {
    tooltipRef.value.showTooltip();
  }

  await delay(2000);

  if (tooltipRef.value) {
    tooltipRef.value.hideTooltip();
  }

  await delay(300);

  showSavedMessage.value = false;
  messageSequenceTimeouts.value = [];
};

const toggleSaved = async (): Promise<void> => {
  if (!props.publishedCourseId && !props.publishedPathId) return;

  if (tooltipRef.value) {
    tooltipRef.value.hideTooltip();
  }

  const contentType = props.publishedCourseId ? 'published_course' : 'published_path';

  const params: SaveParams = {
    content_type: contentType,
    is_active: !isContentSaved.value,
  };

  if (props.publishedCourseId) {
    params.published_course_id = props.publishedCourseId;
  } else if (props.publishedPathId) {
    params.published_path_id = props.publishedPathId;
  }

  try {
    loading.value = true;

    const wasContentSaved = isContentSaved.value;
    
    await ApiService.saveContentForLater(params);

    isContentSaved.value = !isContentSaved.value;

    // Show removal message if content was saved and is now being removed
    if (wasContentSaved && !isContentSaved.value) {
      showRemovalMessageSequence();
    } else if (!wasContentSaved && isContentSaved.value) {
      showSavedMessageSequence();
    } else {
      showRemovalMessage.value = false;
      showSavedMessage.value = false;
    }
  } catch (error: unknown) {
    // Don't show error messages for authentication issues since we redirect users:
    // The API service calls redirectToLoginIfUnauthorized but still throws the error,
    // so we need to detect auth errors and suppress the error message
    const apiError = error as ApiError;
    const isAuthError = apiError?.status === 401 || apiError?.status === 403;
    
    if (!isAuthError) {
      const errorMessage = !isContentSaved.value
        ? props.saveContentErrorMessage
        : props.removeSavedContentErrorMessage;

      const errorType = !isContentSaved.value
        ? 'save-error'
        : 'remove-save-error';

      const errorEvent = new CustomEvent('save-content-error', {
        detail: { 
          type: errorType,
          message: errorMessage
        },
        bubbles: true
      });

      document.dispatchEvent(errorEvent);
    }
  } finally {
    loading.value = false;
  }
};
</script>
